import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Printer, BarChart3, AlertTriangle, Wallet, FileDown, ReceiptText, ChevronDown, ChevronUp, ChevronsUpDown, Sprout } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import UnappliedSplitsBanner from '@/components/UnappliedSplitsBanner';
import NiitExposureCard from '@/components/NiitExposureCard';
import { AccountTypeBadge } from '@/components/AccountTypeModal';
import PdfModeModal, { getSavedPdfMode } from '@/components/PdfModeModal';
import { useActivePortfolioId } from '@/components/PortfolioSelector';
import { ACCOUNT_TYPES, ACCOUNT_TYPE_TONE, getAccountType } from '@/lib/accountTypes';
import { useAuth } from '@/contexts/AuthContext';
import { showReferralToast } from '@/utils/referralToast';
import { toast } from 'sonner';
import { exportNodeAsPdf } from '@/lib/pdfExport';
import { downloadCsv } from '@/lib/csvExport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Year-End Snapshot — full-portfolio printable for tax-season prep.
//
// One row per ticker: cost basis, current value, unrealized G/L,
// realised G/L (closed sells in target year), estimated annual
// dividend income, term split (LT vs ST shares). Top summary block
// rolls everything plus cash flow (contributions in / withdrawals
// out / net) for the year. Print + CSV pipeline mirrors the
// Tax-Harvest Report so the styling is identical.
//
// NOT TAX ADVICE — visible disclaimer banner top + bottom + on every
// printed copy.

const fmt = (v, signed = false) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  const sign = signed ? (n >= 0 ? '+' : '−') : '';
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const pct = (v, decimals = 2) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}${n.toFixed(decimals)}%`;
};

export default function YearEndReport() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const now = new Date();
  // Bookmark-friendly: honour ?year=YYYY on first mount so deep-links
  // (including the cross-link from the Tax Account Audit page) land
  // the user on the correct tax year.
  const initialYear = Number(searchParams.get('year')) || now.getFullYear();
  const [year, setYear] = useState(initialYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Lifted sort state so CSV export mirrors the on-screen sort order.
  const ptbSort = useTableSort();
  const rgrSort = useTableSort();

  // Keep ?year=… in sync with the picker so refreshing or copying the
  // URL preserves the user's current view.
  useEffect(() => {
    const sp = new URLSearchParams(searchParams);
    sp.set('year', String(year));
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  // Round P108r (Feb 2026) — scope the report to the active portfolio.
  const { activeId: activePortfolioId } = useActivePortfolioId();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/portfolio/year-end-report`, {
          params: activePortfolioId ? { year, portfolio_id: activePortfolioId } : { year },
          withCredentials: true,
        });
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || err?.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [year, activePortfolioId]);

  const summary = data?.summary || {};
  const rows = data?.rows || [];
  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) yearOptions.push(y);

  const handlePrint = () => {
    window.print();
    // Tax-season is when DiviDrip's referral loop has the most leverage —
    // the user just produced a useful artifact, so this is the moment to
    // gently nudge them to share. The toast helper itself debounces and
    // only fires after the print dialog releases the UI thread.
    showReferralToast({ kind: 'year-end', user });
  };

  // Round 92o++++++++N++ — explicit "Save PDF" button for tax-prep
  // users who want a single clean file to email their CPA. Mirrors
  // the implementation on the Tax Account Audit page: dynamic-imports
  // html2pdf.js so the ~200KB lib only loads on click; captures the
  // wrapped `#ye-printable` content area on a white background.
  // Print button is left fully intact per the existing UX.
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  // Round 92o++++++++N++++++++ — Delegates to the shared
  // `exportNodeAsPdf` helper so the layout-hardening fixes
  // (badge nowrap, card page-break-avoid, grid stack) apply to
  // every PDF the app produces.
  const handlePdf = async (mode = 'download') => {
    setPdfBusy(true);
    try {
      await exportNodeAsPdf({
        elementId: 'ye-printable',
        filename: `dividrip-year-end-${year}.pdf`,
        mode,
        headerSelector: '.ye-pdf-header',
        user,
        onAfter: () => {
          showReferralToast({ kind: 'year-end', user });
          const hasSavedPref = !!getSavedPdfMode();
          if (hasSavedPref) {
            toast.success('Tip: tap "Change…" anytime to switch your default mode.', {
              action: { label: 'Change…', onClick: () => setPdfModalOpen(true) },
              duration: 4000,
            });
          }
        },
      });
    } finally {
      setPdfBusy(false);
    }
  };
  const handleCsv = () => {
    const headerLine = [
      'Ticker', 'Name', 'Sector', 'Shares',
      'LT Shares', 'ST Shares',
      'Cost Basis', 'Current Value',
      'Unrealized G/L', 'Unrealized G/L %',
      'Realised G/L (' + year + ')',
      'Annual Div Rate $/Sh', 'Estimated Annual Income',
      'Current Yield %',
    ];
    const dataLines = sortItems(rows, ptbSort.sortKey, ptbSort.sortDir, PTB_GETTERS).map(r => [
      r.ticker, r.name, r.sector, r.shares,
      r.lt_shares, r.st_shares,
      r.cost_basis, r.current_value,
      r.unrealized_gain_loss, r.unrealized_gain_loss_pct,
      r.realised_gain_loss,
      r.annual_dividend_rate, r.estimated_annual_income,
      r.dividend_yield,
    ]);
    const totalLine = [
      'TOTAL', '', '', '',
      summary.lt_shares, summary.st_shares,
      summary.total_cost_basis, summary.total_current_value,
      summary.total_unrealized_gain_loss, summary.total_unrealized_gain_loss_pct,
      summary.total_realised_gain_loss,
      '', summary.total_estimated_annual_income, '',
    ];
    // Round 92o++++++++c (Feb 2026) — append a tax-account breakdown
    // block at the bottom of the CSV so users doing tax prep can see
    // at a glance which slice of next-year projected dividends falls
    // inside vs outside taxable accounts (i.e., NIIT exposure).
    const byAccount = summary.annual_income_by_account_type || {};
    const ACCOUNT_LABELS = {
      taxable:         'Taxable Brokerage',
      roth_ira:        'Roth IRA',
      traditional_ira: 'Traditional IRA',
      '401k':          '401(k) / 403(b)',
      hsa:             'HSA',
      other:           'Other / Unspecified',
    };
    const accountLines = [
      [], // visual gap
      ['Projected Annual Dividend Income by Account Type'],
    ];
    Object.entries(ACCOUNT_LABELS).forEach(([key, label]) => {
      const v = Number(byAccount[key] || 0);
      if (v > 0) accountLines.push([label, v]);
    });
    if (typeof summary.annual_income_taxable === 'number') {
      accountLines.push([]);
      accountLines.push(['Taxable Slice (NIIT-applies)', summary.annual_income_taxable]);
      accountLines.push(['Tax-Advantaged Slice (NIIT-exempt)', summary.annual_income_tax_advantaged ?? 0]);
    }
    // Round 95b (Jun 2026) — Realized Gains Report block: every sale
    // of the year with basis/term + the ST/LT netting waterfall, so
    // the CSV alone is enough for 1099-B reconciliation.
    const rs = data?.realized_sales;
    const rgLines = [];
    if (rs?.sales?.length) {
      const n = rs.netting || {};
      rgLines.push([], [`Realized Gains Report (mini 1099-B) — ${year}`]);
      rgLines.push(['Date', 'Ticker', 'Shares', 'Sale Price', 'Proceeds', 'Cost Basis', 'Gain/Loss', 'Term', 'Basis Source', 'Method', 'Wash-Sale Risk']);
      sortItems(rs.sales, rgrSort.sortKey, rgrSort.sortDir, RGR_GETTERS).forEach(s => rgLines.push([
        s.date, s.ticker, s.shares, s.price, s.proceeds, s.cost_basis, s.gain_loss,
        s.term || 'untagged', s.exact_basis ? 'exact lot' : 'weighted-avg est.',
        s.method, s.wash_sale_risk ? 'YES' : '',
      ]));
      rgLines.push([]);
      rgLines.push(['Net Short-Term', n.net_st]);
      rgLines.push(['Net Long-Term', n.net_lt]);
      if (n.untagged_count) rgLines.push(['Untagged (estimated basis)', n.untagged_net]);
      rgLines.push(['Combined Net', n.combined_net]);
      rgLines.push(['Offsets Ordinary Income (max $3,000)', n.ordinary_income_offset]);
      rgLines.push([`Loss Carryforward to ${year + 1}`, n.loss_carryforward]);
    }
    downloadCsv(`dividrip-year-end-snapshot-${year}.csv`, [headerLine, ...dataLines, totalLine, ...accountLines, ...rgLines]);
    showReferralToast({ kind: 'year-end', user });
  };

  return (
    <div className="min-h-screen min-h-[100svh] bg-background text-foreground">
      <style>{`
        /* Round 92o++++++++N++++++++++++ (Feb 2026) — unified
           PRINT + PDF layout & theme normalization for the Year-End
           Snapshot.

           User pain points closed by this block:
             1. PDF still rendered 6 top tiles in one row (unusable
                on A4). Print already had print:grid-cols-3 — mirror
                it for the pdf-rendering body class too.
             2. Print output included the global <AppHeader /> and
                the sonner toast portal — cluttered the tax-prep
                artifact. PDF was clean because html2canvas captures
                a single node, but browser Print used the whole page.
             3. Dark theme users got a "washed out, hard-to-read"
                PDF/Print at the top of the page. Force LIGHT theme
                on the report content regardless of the app's active
                theme so the tax pro always receives a legible copy.
        */

        /* --- 1. Print rules (window.print) ------------------------ */
        @media print {
          .no-print { display: none !important; }
          /* Site chrome + toast portal must never appear in the
             printed artifact — user only wants the report content. */
          header,
          [data-sonner-toaster],
          [data-sonner-toaster] * {
            display: none !important;
          }
          body { background: white !important; color: black !important; }
          .print-clean { background: white !important; color: black !important; box-shadow: none !important; border-color: #ccc !important; }
          a { color: black !important; text-decoration: none !important; }
          /* Force light theme on the report content — dark-mode
             tailwind utilities on children get overridden below. */
          #ye-printable,
          #ye-printable * {
            color: black !important;
            background-color: transparent !important;
            border-color: #ccc !important;
          }
          #ye-printable .print-clean,
          #ye-printable [data-pdf-keep] {
            background-color: #ffffff !important;
          }
        }

        /* Rows hidden behind SHOW MORE stay in the DOM so Print, PDF
           export and CSV always contain the FULL dataset, even when
           the user never expanded the table on screen. */
        .ye-collapsed-row { display: none; }
        @media print { .ye-collapsed-row { display: table-row !important; } }
        body.pdf-rendering .ye-collapsed-row { display: table-row !important; }
        body.pdf-rendering .no-print { display: none !important; }

        /* --- 2. PDF rules (html2pdf.js via body.pdf-rendering) ---- */
        /* Same layout constraints the print media block applies —
           mirrored here so the html2pdf snapshot renders identically. */
        body.pdf-rendering #ye-printable .grid.lg\\:grid-cols-6 {
          grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
        }
        body.pdf-rendering #ye-printable .grid.md\\:grid-cols-4 {
          grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
        }

        /* Force light theme inside the PDF snapshot regardless of the
           app's current dark/light mode. html2canvas sees whatever
           the DOM is at render time — we override to a legible white
           background + dark text for every child. Individual tint
           accents (gain green, loss red, brand cyan, etc.) come from
           inline styles / hex values elsewhere and survive. */
        body.pdf-rendering #ye-printable,
        body.pdf-rendering #ye-printable * {
          color: #0f172a !important;
          border-color: #d1d5db !important;
        }
        body.pdf-rendering #ye-printable,
        body.pdf-rendering #ye-printable .bg-background,
        body.pdf-rendering #ye-printable .bg-card,
        body.pdf-rendering #ye-printable .bg-muted,
        body.pdf-rendering #ye-printable .bg-muted\\/30,
        body.pdf-rendering #ye-printable .bg-muted\\/40,
        body.pdf-rendering #ye-printable .bg-muted\\/50,
        body.pdf-rendering #ye-printable .print-clean,
        body.pdf-rendering #ye-printable [data-pdf-keep] {
          background-color: #ffffff !important;
        }
        /* Preserve gain-green / loss-red / brand-cyan text tints —
           these are informational, not chrome. Override the blanket
           color:#0f172a from above for the semantic classes only. */
        body.pdf-rendering #ye-printable .text-emerald-600,
        body.pdf-rendering #ye-printable .text-emerald-500,
        body.pdf-rendering #ye-printable .text-emerald-700 { color: #059669 !important; }
        body.pdf-rendering #ye-printable .text-red-600,
        body.pdf-rendering #ye-printable .text-red-500,
        body.pdf-rendering #ye-printable .text-red-700 { color: #dc2626 !important; }
        body.pdf-rendering #ye-printable .text-cyan-600,
        body.pdf-rendering #ye-printable .text-cyan-500,
        body.pdf-rendering #ye-printable .text-cyan-700 { color: #0891b2 !important; }
        body.pdf-rendering #ye-printable .text-amber-600,
        body.pdf-rendering #ye-printable .text-amber-500,
        body.pdf-rendering #ye-printable .text-amber-700 { color: #d97706 !important; }
        body.pdf-rendering #ye-printable .text-violet-600,
        body.pdf-rendering #ye-printable .text-violet-500,
        body.pdf-rendering #ye-printable .text-violet-700 { color: #7c3aed !important; }
        body.pdf-rendering #ye-printable .text-muted-foreground { color: #64748b !important; }
      `}</style>

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 pt-6 pb-0">
        <div className="flex items-center justify-between mb-6 gap-2 no-print flex-wrap">
          <div className="flex items-center gap-3">
            <BackOrHomeButton testid="ye-back-or-home-btn" showEscHint={false} />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                <BarChart3 className="w-7 h-7 text-cyan-600 dark:text-cyan-400" />
                Year-End Snapshot
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                One-page portfolio summary for tax-season prep — print or export to hand to your tax pro.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 px-2 rounded-lg border bg-card text-sm font-mono font-bold"
              data-testid="ye-year-select"
            >
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <Link
              to={`/tax-account-audit?year=${year}`}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border bg-card hover:bg-muted text-sm font-semibold transition-colors no-print"
              data-testid="ye-audit-link"
              title="Open the dedicated Tax Account Audit — performance & dividends per account_type"
            >
              <Wallet className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
              Account Audit
            </Link>
            <button
              type="button"
              onClick={handleCsv}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border bg-card hover:bg-muted text-sm font-semibold transition-colors disabled:opacity-50"
              disabled={!rows.length}
              data-testid="ye-csv-btn"
              title="Download as CSV — opens in Excel / Google Sheets"
            >
              <Download className="w-3.5 h-3.5" />
              CSV
            </button>
            <button
              type="button"
              onClick={() => {
                const saved = getSavedPdfMode();
                if (saved) {
                  handlePdf(saved);
                } else {
                  setPdfModalOpen(true);
                }
              }}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border bg-card hover:bg-muted text-sm font-semibold transition-colors disabled:opacity-50"
              disabled={!rows.length || pdfBusy}
              data-testid="ye-pdf-btn"
              title="Download the PDF or open it in a new tab"
            >
              <FileDown className="w-3.5 h-3.5" />
              {pdfBusy ? 'Building…' : 'Save/View PDF'}
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold transition-colors disabled:opacity-50"
              disabled={!rows.length}
              data-testid="ye-print-btn"
              title="Print or save as PDF"
            >
              <Printer className="w-3.5 h-3.5" />
              Print
            </button>
          </div>
        </div>

        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-bold">Year-End Portfolio Snapshot — {year}</h1>
          <p className="text-xs text-gray-600">
            Generated by DiviDrip (twylightcrow.com) · {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Feb 2026 — Warn before displaying any tax numbers if a
            split hasn't been applied to the user's lots yet. */}
        <div className="print:hidden">
          <UnappliedSplitsBanner portfolioId={activePortfolioId} />
        </div>

        {/* id="ye-printable" wraps the body so html2pdf captures the
            report (disclaimer through bottom) without including the
            no-print toolbar above. */}
        <div id="ye-printable" data-pdf-root>
          <div className="hidden ye-pdf-header mb-3">
            <h2 className="text-xl font-bold text-black">Year-End Portfolio Snapshot — {year}</h2>
            <p className="text-[11px] text-gray-700">
              Generated by DiviDrip · {new Date().toLocaleDateString()}
            </p>
          </div>
        <div className="rounded-lg border border-amber-300/60 bg-amber-50/50 dark:border-amber-700/50 dark:bg-amber-950/20 p-3 mb-5 text-[11px] text-amber-900 dark:text-amber-200 print-clean" data-testid="ye-disclaimer">
          <strong>Educational record, not tax advice.</strong> Cost basis comes from your DiviDrip-entered or CSV-imported data. Current value reflects the latest cached price. Realised gains and losses are calculated from sells closed during the calendar year you selected; for sells without a tagged cost basis, DiviDrip uses the weighted-average cost from the remaining lots at sell time — your broker&rsquo;s 1099-B may use a different lot-selection method. Always reconcile with the official 1099-B. Dividend income shown is forward-looking (current shares × current annual dividend rate); actual cash dividends received during the year may differ if rates changed mid-year or if you held different share counts at different times.
        </div>

        {loading && (
          <div className="rounded-lg border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground" data-testid="ye-loading">
            Loading {year} snapshot…
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-rose-300/60 bg-rose-50/50 dark:bg-rose-950/20 p-4 text-sm text-rose-700 dark:text-rose-300 flex items-start gap-2" data-testid="ye-error">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <strong>Couldn&rsquo;t load the snapshot.</strong>
              <div className="mt-1">{error}</div>
            </div>
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div className="rounded-lg border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground" data-testid="ye-empty">
            No portfolio positions to report for {year}. Add positions or import a CSV from the Portfolio page.
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            {/* Summary tiles.
                Print/PDF: `print:grid-cols-3` splits the 6 tiles into
                two rows of 3 so the letter-size print sheet doesn't
                clip the last three (Feb 2026 fix — 6 columns squeezed
                past the printable width and cut off Forward Annual
                Income entirely). */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6 print:grid-cols-3">
              <Tile label="Positions" value={summary.position_count} testId="ye-tile-positions" />
              <Tile label="Total Cost Basis" value={fmt(summary.total_cost_basis)} testId="ye-tile-cost" />
              <Tile label="Total Current Value" value={fmt(summary.total_current_value)} testId="ye-tile-value" />
              <Tile
                label="Unrealized G/L"
                value={fmt(summary.total_unrealized_gain_loss, true)}
                sub={pct(summary.total_unrealized_gain_loss_pct)}
                accent={(summary.total_unrealized_gain_loss || 0) >= 0 ? 'emerald' : 'rose'}
                testId="ye-tile-unrealized"
              />
              <Tile
                label={`Realised G/L (${year})`}
                value={fmt(summary.total_realised_gain_loss, true)}
                sub={`${fmt(summary.realised_loss_total)} loss · ${fmt(summary.realised_gain_total)} gain`}
                accent={(summary.total_realised_gain_loss || 0) >= 0 ? 'emerald' : 'rose'}
                testId="ye-tile-realised"
              />
              <Tile
                label="Forward Annual Income"
                value={fmt(summary.total_estimated_annual_income)}
                sub="estimated"
                accent="emerald"
                testId="ye-tile-income"
              />
            </div>

            {/* Cash flow + tax-term split — secondary row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 print:grid-cols-4">
              <Tile label="Outside Deposits" value={fmt(summary.deposits_outside)} sub={`${year} contributions in`} testId="ye-tile-deposits" />
              <Tile label="Outside Withdrawals" value={fmt(summary.withdrawals_outside)} sub={`${year} withdrawn to bank`} testId="ye-tile-withdraws" />
              <Tile label="Net Contributions" value={fmt(summary.net_contributions, true)} accent={(summary.net_contributions || 0) >= 0 ? 'emerald' : 'rose'} sub={`${year} net cash in`} testId="ye-tile-net-contrib" />
              <Tile label="Cash Balance" value={fmt(summary.cash_balance)} sub="current" testId="ye-tile-cash" />
            </div>

            {/* NIIT (3.8% Net Investment Income Tax) exposure card */}
            <NiitExposureCard
              projectedDividendIncome={summary.total_estimated_annual_income}
              taxableDividendIncome={summary.annual_income_taxable}
              taxAdvantagedDividendIncome={summary.annual_income_tax_advantaged}
            />

            {/* Account Mix breakdown — when there's any tax-advantaged
                income, render a compact row showing how the projected
                dividends split across account types. Skipped when 100%
                of projected dividends are in a single account type
                (almost always "taxable" — the default). */}
            <AccountMixRow byAccount={summary.annual_income_by_account_type} />

            {/* Per-ticker table */}
            <PerTickerBreakdown rows={rows} summary={summary} sort={ptbSort} />

            {/* Realized Gains Report — mini 1099-B (Round 95b, Jun 2026) */}
            <RealizedGainsReport realized={data?.realized_sales} year={year} sort={rgrSort} />
          </>
        )}
        </div>{/* /#ye-printable */}
      </div>

      <div className="no-print">
        <SiteDisclaimer />
      </div>
      <PdfModeModal
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        onApply={(mode) => handlePdf(mode)}
        title="Save / View Year-End Report PDF"
      />
    </div>
  );
}

// Sortable-table helpers (Jun 2026) — shared by the Per-Ticker
// Breakdown and Realized Gains Report tables.
function useTableSort() {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const onSort = (key, numeric) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(numeric ? 'desc' : 'asc');
    }
  };
  return { sortKey, sortDir, onSort };
}

function sortItems(items, key, dir, getters) {
  if (!key) return items;
  const get = (getters && getters[key]) || ((r) => r[key]);
  const mul = dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const va = get(a);
    const vb = get(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === 'string' || typeof vb === 'string') return String(va).localeCompare(String(vb)) * mul;
    return (va - vb) * mul;
  });
}

function SortTh({ children, align, tip, k, numeric, sort, testId }) {
  const active = sort.sortKey === k;
  return (
    <th
      className={`px-3 py-2 text-[11px] uppercase tracking-wider font-bold cursor-pointer select-none transition-colors hover:text-foreground ${active ? 'text-foreground' : 'text-muted-foreground'} ${align === 'right' ? 'text-right' : 'text-left'}`}
      title={tip}
      onClick={() => sort.onSort(k, numeric)}
      data-testid={testId}
    >
      <span className={`inline-flex items-center gap-0.5 whitespace-nowrap ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {children}
        {active
          ? (sort.sortDir === 'asc'
            ? <ChevronUp className="w-3 h-3 shrink-0" />
            : <ChevronDown className="w-3 h-3 shrink-0" />)
          : <ChevronsUpDown className="w-3 h-3 shrink-0 opacity-40" />}
      </span>
    </th>
  );
}

// Per-Ticker Breakdown — sortable headers + 10-row taste with the
// app's standard SHOW MORE / SHOW LESS toggle (Jun 2026).
const PTB_PREVIEW_ROWS = 10;
const numGet = (k) => (r) => (r[k] == null ? null : Number(r[k]));
const PTB_GETTERS = {
  ticker: (r) => r.ticker || '',
  sector: (r) => r.sector || null,
  shares: numGet('shares'),
  lt_shares: numGet('lt_shares'),
  st_shares: numGet('st_shares'),
  cost_basis: numGet('cost_basis'),
  current_value: numGet('current_value'),
  unrealized_gain_loss: numGet('unrealized_gain_loss'),
  unrealized_gain_loss_pct: numGet('unrealized_gain_loss_pct'),
  realised_gain_loss: numGet('realised_gain_loss'),
  estimated_annual_income: numGet('estimated_annual_income'),
  account: (r) => r.dominant_account || r.historical_dominant_account || null,
};

function PerTickerBreakdown({ rows, summary, sort }) {
  const [expanded, setExpanded] = useState(false);
  const sorted = sortItems(rows, sort.sortKey, sort.sortDir, PTB_GETTERS);
  const hiddenCount = rows.length - PTB_PREVIEW_ROWS;

  return (
    <div className="rounded-xl border bg-card overflow-hidden print-clean" data-testid="ye-table">
      <h3 className="text-sm font-bold px-4 py-2 border-b bg-muted/40 print-clean">Per-Ticker Breakdown</h3>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1080px] text-sm">
                  <thead className="bg-muted/30 print-clean">
                    <tr>
                      <SortTh k="ticker" sort={sort} testId="ye-ptb-sort-ticker">Ticker</SortTh>
                      <SortTh k="sector" sort={sort} testId="ye-ptb-sort-sector">Sector</SortTh>
                      <SortTh k="shares" numeric align="right" sort={sort} testId="ye-ptb-sort-shares">Shares</SortTh>
                      <SortTh k="lt_shares" numeric align="right" sort={sort} tip="Long-term shares (held > 365 days)" testId="ye-ptb-sort-lt">LT</SortTh>
                      <SortTh k="st_shares" numeric align="right" sort={sort} tip="Short-term shares (held ≤ 365 days)" testId="ye-ptb-sort-st">ST</SortTh>
                      <SortTh k="cost_basis" numeric align="right" sort={sort} testId="ye-ptb-sort-cost">Cost Basis</SortTh>
                      <SortTh k="current_value" numeric align="right" sort={sort} testId="ye-ptb-sort-value">Current Value</SortTh>
                      <SortTh k="unrealized_gain_loss" numeric align="right" sort={sort} testId="ye-ptb-sort-unrl">Unrealized G/L</SortTh>
                      <SortTh k="unrealized_gain_loss_pct" numeric align="right" sort={sort} testId="ye-ptb-sort-unrlpct">Unrl %</SortTh>
                      <SortTh k="realised_gain_loss" numeric align="right" sort={sort} testId="ye-ptb-sort-realised">Realised G/L</SortTh>
                      <SortTh k="estimated_annual_income" numeric align="right" sort={sort} testId="ye-ptb-sort-income">Est. Annual Income</SortTh>
                      <SortTh k="account" sort={sort} testId="ye-ptb-sort-account">Account</SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((r, i) => (
                      <tr key={r.ticker} className={`border-t border-border odd:bg-muted/20${!expanded && i >= PTB_PREVIEW_ROWS ? ' ye-collapsed-row' : ''}`} data-testid={`ye-row-${r.ticker}`}>
                        <td className="px-3 py-2 font-mono font-bold">{r.ticker}<div className="text-[10px] text-muted-foreground font-sans truncate max-w-[160px]">{r.name}</div>{r.is_treasury_vehicle && <div className="text-[9px] font-sans font-semibold text-indigo-600 dark:text-indigo-400 cursor-help" title="Treasury-vehicle preferred — distributions are Return of Capital. They lower your cost basis instead of counting as taxable dividend income (taxable accounts only)." data-testid={`ye-roc-note-${r.ticker}`}>ROC — lowers cost basis</div>}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{r.sector || '—'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{Number(r.shares || 0).toFixed(4)}</td>
                        <td className="px-3 py-2 text-right font-mono text-[11px] text-cyan-700 dark:text-cyan-400">{Number(r.lt_shares || 0).toFixed(4)}</td>
                        <td className="px-3 py-2 text-right font-mono text-[11px] text-amber-700 dark:text-amber-400">{Number(r.st_shares || 0).toFixed(4)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{fmt(r.cost_basis)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{fmt(r.current_value)}</td>
                        <td className={`px-3 py-2 text-right font-mono text-xs font-bold ${r.unrealized_gain_loss == null ? 'text-muted-foreground' : r.unrealized_gain_loss >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{fmt(r.unrealized_gain_loss, true)}</td>
                        <td className={`px-3 py-2 text-right font-mono text-xs ${r.unrealized_gain_loss_pct == null ? 'text-muted-foreground' : r.unrealized_gain_loss_pct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{pct(r.unrealized_gain_loss_pct)}</td>
                        <td className={`px-3 py-2 text-right font-mono text-xs font-bold ${r.realised_gain_loss == 0 ? 'text-muted-foreground' : r.realised_gain_loss > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{r.realised_gain_loss ? fmt(r.realised_gain_loss, true) : '—'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-emerald-700 dark:text-emerald-400">{fmt(r.estimated_annual_income)}</td>
                        <td className="px-3 py-2"><DominantAccountCell row={r} /></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border bg-muted/40 font-bold print-clean">
                      <td className="px-3 py-2 text-xs uppercase tracking-wider" colSpan={2}>TOTAL</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">—</td>
                      <td className="px-3 py-2 text-right font-mono text-[11px]">{Number(summary.lt_shares || 0).toFixed(4)}</td>
                      <td className="px-3 py-2 text-right font-mono text-[11px]">{Number(summary.st_shares || 0).toFixed(4)}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{fmt(summary.total_cost_basis)}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{fmt(summary.total_current_value)}</td>
                      <td className={`px-3 py-2 text-right font-mono text-xs ${summary.total_unrealized_gain_loss >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{fmt(summary.total_unrealized_gain_loss, true)}</td>
                      <td className={`px-3 py-2 text-right font-mono text-xs ${summary.total_unrealized_gain_loss_pct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{pct(summary.total_unrealized_gain_loss_pct)}</td>
                      <td className={`px-3 py-2 text-right font-mono text-xs ${summary.total_realised_gain_loss >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{fmt(summary.total_realised_gain_loss, true)}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-emerald-700 dark:text-emerald-400">{fmt(summary.total_estimated_annual_income)}</td>
                      <td className="px-3 py-2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
      {rows.length > PTB_PREVIEW_ROWS && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="w-full py-1.5 flex items-center justify-center gap-1.5 text-[11px] font-bold tracking-wider text-violet-600 dark:text-violet-400 hover:bg-muted/40 transition-colors border-t border-border no-print"
          data-testid={expanded ? 'ye-ptb-show-less' : 'ye-ptb-show-more'}
        >
          {expanded ? (
            <><ChevronUp className="w-3.5 h-3.5" /> SHOW LESS <ChevronUp className="w-3.5 h-3.5" /></>
          ) : (
            <><ChevronDown className="w-3.5 h-3.5" /> SHOW MORE ({hiddenCount} MORE) <ChevronDown className="w-3.5 h-3.5" /></>
          )}
        </button>
      )}
    </div>
  );
}

// Round 95b (Jun 2026) — Realized Gains Report (mini 1099-B).
// Sell-by-sell detail with exact basis + ST/LT term (P95 multi-sell
// rows), 1099-B-style netting boxes, and the $3,000 ordinary-income
// offset meter. Table shows a 4-row taste by default with the app's
// standard SHOW MORE / SHOW LESS toggle.
const RGR_PREVIEW_ROWS = 4;
const RGR_GETTERS = {
  date: (s) => s.date || '',
  ticker: (s) => s.ticker || '',
  shares: numGet('shares'),
  proceeds: numGet('proceeds'),
  cost_basis: numGet('cost_basis'),
  gain_loss: numGet('gain_loss'),
  term: (s) => s.term || null,
  method: (s) => s.method || null,
};

function RealizedGainsReport({ realized, year, sort }) {
  const [expanded, setExpanded] = useState(false);
  const sales = sortItems(realized?.sales || [], sort.sortKey, sort.sortDir, RGR_GETTERS);
  const n = realized?.netting || {};
  const hiddenCount = sales.length - RGR_PREVIEW_ROWS;
  const netLoss = (n.combined_net || 0) < 0;
  const meterPct = netLoss ? Math.min(100, ((n.ordinary_income_offset || 0) / (n.offset_cap || 3000)) * 100) : 0;
  const glCls = (v) => v < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400';

  return (
    <div className="rounded-xl border bg-card overflow-hidden print-clean mt-6" data-testid="ye-rgr">
      <h3 className="text-sm font-bold px-4 py-2 border-b bg-muted/40 print-clean flex items-center gap-2">
        <ReceiptText className="w-4 h-4 text-violet-600 dark:text-violet-400" />
        Realized Gains Report
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">mini 1099-B · {year}</span>
      </h3>

      {sales.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-muted-foreground" data-testid="ye-rgr-empty">
          No sells recorded in {year}. Every sale you make — per-lot or via Multi-Share Sell — will
          appear here with its exact cost basis, ST/LT term, and the $3,000 ordinary-income offset math.
        </div>
      ) : (
        <div className="p-4 space-y-3">
          {/* Netting boxes */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ye-rgr-netting">
            <NetBox label="Net Short-Term" value={n.net_st} sub={`+${fmt(n.st_gains)} · −${fmt(n.st_losses)}`} tip="Short-term gains net against short-term losses first. Net ST gains are taxed at your ordinary income rate." />
            <NetBox label="Net Long-Term" value={n.net_lt} sub={`+${fmt(n.lt_gains)} · −${fmt(n.lt_losses)}`} tip="Long-term gains net against long-term losses first. Net LT gains get the preferential 0/15/20% rate." />
            {n.untagged_count > 0 && (
              <NetBox label={`Untagged (est. × ${n.untagged_count})`} value={n.untagged_net} sub="weighted-avg basis" tip="Sales recorded before exact lot-tagging existed. Basis is the weighted-average estimate — reconcile with your broker's 1099-B." />
            )}
            <NetBox label="Combined Net" value={n.combined_net} bold tip="Net ST + Net LT (+ untagged). A combined net loss offsets up to $3,000 of ordinary income; the rest carries forward." />
          </div>

          {/* $3,000 ordinary-income offset meter */}
          <div
            className={`rounded-lg border p-3 ${netLoss ? 'border-emerald-300/60 bg-emerald-50/40 dark:border-emerald-700/50 dark:bg-emerald-950/20' : 'border-border bg-muted/20 opacity-80'}`}
            data-testid="ye-rgr-meter"
          >
            <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">
              <span>$3,000 ordinary-income offset</span>
              <span data-testid="ye-rgr-meter-used">{netLoss ? `${fmt(n.ordinary_income_offset)} of $3,000 used` : '$0 of $3,000 used'}</span>
            </div>
            <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${netLoss ? 'bg-emerald-500' : 'bg-zinc-400/40'}`}
                style={{ width: `${meterPct}%` }}
              />
            </div>
            <p className="text-[11px] mt-1.5 text-muted-foreground">
              {netLoss ? (
                <>
                  Net loss of <strong className="text-rose-600 dark:text-rose-400">{fmt(Math.abs(n.combined_net))}</strong> →{' '}
                  <strong className="text-emerald-700 dark:text-emerald-300">{fmt(n.ordinary_income_offset)}</strong> deducts against ordinary income this year
                  {n.loss_carryforward > 0
                    ? <>; <strong>{fmt(n.loss_carryforward)}</strong> carries forward to {year + 1}.</>
                    : <>; {fmt(n.offset_headroom)} of headroom left if you harvest more losses before Dec 31.</>}
                </>
              ) : (
                <>
                  No loss to offset — net gain year of <strong className="text-emerald-600 dark:text-emerald-400">{fmt(n.combined_net, true)}</strong>.
                  Harvested losses would appear here: they offset capital gains first, then up to $3,000 of ordinary
                  income per year, with the remainder carried forward indefinitely.
                </>
              )}
            </p>
            {/* Harvest-candidates deep link — turns the meter from a
                scoreboard into a to-do list. Hidden when the $3,000 cap
                is already maxed (carryforward > 0 means no headroom). */}
            {(!netLoss || n.offset_headroom > 0) && (
              <Link
                to={`/tax-lots?filter=harvest&from=year-end&headroom=${netLoss ? (n.offset_headroom || 0) : (n.offset_cap || 3000)}`}
                className="no-print inline-flex items-center gap-1.5 mt-2 text-[11px] font-bold text-violet-600 dark:text-violet-400 hover:underline"
                data-testid="ye-rgr-harvest-link"
                title="Open the Tax Lot Optimizer pre-filtered to loss lots with no wash-sale risk, biggest losses first"
              >
                <Sprout className="w-3.5 h-3.5" />
                {netLoss
                  ? `Find harvest candidates to fill the remaining ${fmt(n.offset_headroom)} →`
                  : 'Find harvest candidates →'}
              </Link>
            )}
          </div>

          {/* Sell-by-sell table — 4-row taste + Show More/Less */}
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ye-rgr-table">
                <thead className="bg-muted/30 print-clean">
                  <tr>
                    <SortTh k="date" sort={sort} testId="ye-rgr-sort-date">Date</SortTh>
                    <SortTh k="ticker" sort={sort} testId="ye-rgr-sort-ticker">Ticker</SortTh>
                    <SortTh k="shares" numeric align="right" sort={sort} testId="ye-rgr-sort-shares">Shares</SortTh>
                    <SortTh k="proceeds" numeric align="right" sort={sort} testId="ye-rgr-sort-proceeds">Proceeds</SortTh>
                    <SortTh k="cost_basis" numeric align="right" sort={sort} tip="Exact lot basis when tagged; weighted-average estimate (marked est.) otherwise" testId="ye-rgr-sort-cost">Cost Basis</SortTh>
                    <SortTh k="gain_loss" numeric align="right" sort={sort} testId="ye-rgr-sort-gl">Gain/Loss</SortTh>
                    <SortTh k="term" sort={sort} tip="ST = held ≤ 1 year (ordinary rates) · LT = held > 1 year (0/15/20% rates) · est. = untagged sale" testId="ye-rgr-sort-term">Term</SortTh>
                    <SortTh k="method" sort={sort} tip="Cost-basis method used (Multi-Share Sell) or how the sale was recorded" testId="ye-rgr-sort-method">Method</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((s, i) => (
                    <tr key={`${s.date}-${s.ticker}-${i}`} className={`border-t border-border odd:bg-muted/20${!expanded && i >= RGR_PREVIEW_ROWS ? ' ye-collapsed-row' : ''}`} data-testid={`ye-rgr-row-${i}`}>
                      <td className="px-3 py-1.5 font-mono text-xs">{s.date}</td>
                      <td className="px-3 py-1.5 font-mono text-xs font-bold">{s.ticker}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-xs">{Number(s.shares || 0).toFixed(4)}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-xs">{fmt(s.proceeds)}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-xs">
                        {fmt(s.cost_basis)}
                        {!s.exact_basis && <span className="ml-1 text-[9px] italic text-muted-foreground">est.</span>}
                      </td>
                      <td className={`px-3 py-1.5 text-right font-mono text-xs font-bold ${glCls(s.gain_loss)}`}>
                        <span className="inline-flex items-center gap-1">
                          {s.wash_sale_risk && (
                            <AlertTriangle
                              className="w-3 h-3 text-amber-500"
                              data-testid={`ye-rgr-wash-${i}`}
                              aria-label="Potential wash sale"
                            />
                          )}
                          {fmt(s.gain_loss, true)}
                        </span>
                      </td>
                      <td className="px-3 py-1.5">
                        {s.term ? (
                          <span
                            className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold leading-none cursor-help ${s.term === 'LT' ? 'bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300' : 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300'}`}
                            title={s.term === 'LT'
                              ? 'LT = Long-Term: held more than 1 year. Gains get the preferential 0/15/20% capital-gains rate.'
                              : 'ST = Short-Term: held 1 year or less. Gains are taxed at your ordinary income rate (up to 37% federal).'}
                          >
                            {s.term}
                          </span>
                        ) : (
                          <span className="text-[9px] italic text-muted-foreground">est.</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase">{s.method}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {sales.length > RGR_PREVIEW_ROWS && (
              <button
                type="button"
                onClick={() => setExpanded(v => !v)}
                className="w-full py-1.5 flex items-center justify-center gap-1.5 text-[11px] font-bold tracking-wider text-violet-600 dark:text-violet-400 hover:bg-muted/40 transition-colors border-t border-border no-print"
                data-testid={expanded ? 'ye-rgr-show-less' : 'ye-rgr-show-more'}
              >
                {expanded ? (
                  <><ChevronUp className="w-3.5 h-3.5" /> SHOW LESS <ChevronUp className="w-3.5 h-3.5" /></>
                ) : (
                  <><ChevronDown className="w-3.5 h-3.5" /> SHOW MORE ({hiddenCount} MORE) <ChevronDown className="w-3.5 h-3.5" /></>
                )}
              </button>
            )}
          </div>

          <p className="text-[10px] text-muted-foreground">
            <span className="italic">est.</span> = sale recorded without exact lot tagging — basis is the weighted-average
            estimate. <AlertTriangle className="inline w-3 h-3 text-amber-500 -mt-0.5" /> = potential wash sale (same-ticker
            buy within ±30 days of a loss sale — the deduction may be disallowed). Not tax advice — reconcile with your
            broker&apos;s official 1099-B.
          </p>
        </div>
      )}
    </div>
  );
}

function NetBox({ label, value, sub, bold, tip }) {
  const v = Number(value) || 0;
  return (
    <div className={`rounded-md border px-2.5 py-2 ${bold ? 'border-2 border-border bg-muted/40' : 'border-border bg-muted/20'}`} title={tip}>
      <div className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className={`font-mono font-bold text-sm ${v < 0 ? 'text-rose-600 dark:text-rose-400' : v > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
        {fmt(v, true)}
      </div>
      {sub && <div className="text-[9px] text-muted-foreground font-mono">{sub}</div>}
    </div>
  );
}

// Shows the dominant account type for a ticker's lots. When all lots
// share one account → just the badge. When mixed (2+ types holding
// shares of the same ticker) → a "Mixed" badge with the breakdown in
// the title attribute so a hover reveals shares per account type.
// Round 92o++++++++e (Feb 2026).
function DominantAccountCell({ row }) {
  const dom = row?.dominant_account;
  // Sell-only fallback: when there are no open lots but we still know
  // the historical account from cash_transactions sell records, show
  // a faded "was: <type>" pill so tax-prep users can audit which
  // account a closed position used to live in.
  if (!dom) {
    const hist = row?.historical_dominant_account;
    if (hist) {
      const at = getAccountType(hist);
      const Icon = at.icon;
      return (
        <span
          className="inline-flex items-center gap-1 rounded-full ring-1 px-2 py-0.5 text-[10px] font-semibold opacity-60 italic bg-zinc-50 text-zinc-600 ring-zinc-300 dark:bg-zinc-900/40 dark:text-zinc-400 dark:ring-zinc-700"
          title={`Position closed. Last lot was in ${at.label}.`}
          data-testid={`ye-account-historical-${row.ticker}`}
        >
          <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
          was: {at.short}
        </span>
      );
    }
    return <span className="text-muted-foreground text-[10px]">—</span>;
  }
  if (dom === 'mixed') {
    const parts = Object.entries(row.account_breakdown || {})
      .sort((a, b) => Number(b[1]) - Number(a[1]))
      .map(([k, v]) => `${getAccountType(k).short}: ${Number(v).toFixed(4)} sh`);
    const tip = `Held across ${parts.length} accounts — ${parts.join(' · ')}`;
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full ring-1 px-2 py-0.5 text-[10px] font-semibold bg-zinc-100 text-zinc-700 ring-zinc-300 dark:bg-zinc-800/60 dark:text-zinc-200 dark:ring-zinc-600"
        title={tip}
        data-testid={`ye-account-mixed-${row.ticker}`}
      >
        Mixed ({parts.length})
      </span>
    );
  }
  return <AccountTypeBadge value={dom} size="xs" testId={`ye-account-${row.ticker}`} />;
}


function Tile({ label, value, sub, accent = 'neutral', testId }) {
  const accentCls =
    accent === 'rose' ? 'border-rose-300/60 bg-rose-50/40 dark:border-rose-700/50 dark:bg-rose-950/20'
    : accent === 'emerald' ? 'border-emerald-300/60 bg-emerald-50/40 dark:border-emerald-700/50 dark:bg-emerald-950/20'
    : 'border-border bg-muted/30';
  const valueCls =
    accent === 'rose' ? 'text-rose-700 dark:text-rose-300'
    : accent === 'emerald' ? 'text-emerald-700 dark:text-emerald-300'
    : 'text-foreground';
  return (
    <div className={`rounded-xl border-2 p-3 print-clean ${accentCls}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className={`text-lg font-mono font-bold mt-1 ${valueCls}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function AccountMixRow({ byAccount }) {
  if (!byAccount || typeof byAccount !== 'object') return null;
  const entries = Object.entries(byAccount).filter(([_, v]) => Number(v) > 0);
  const total = entries.reduce((s, [_, v]) => s + Number(v || 0), 0);
  // Skip the row when nothing splits — e.g. brand-new users or 100%
  // single-account portfolios already get the message via the NIIT
  // tile's caveat banner.
  if (entries.length < 2 || total <= 0) return null;
  return (
    <div className="rounded-xl border bg-card p-4 mb-6 print-clean" data-testid="ye-account-mix">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
        <h3 className="text-sm font-bold tracking-tight">Projected Dividend Income by Account Type</h3>
        <span className="text-[10px] text-muted-foreground">total ${total.toLocaleString(undefined, { maximumFractionDigits: 2 })}/yr</span>
      </div>
      <div className="flex w-full h-2 rounded-full overflow-hidden mb-3" data-testid="ye-account-mix-bar">
        {entries.map(([k, v]) => {
          const at = getAccountType(k);
          const pct = (Number(v) / total) * 100;
          const tone = (ACCOUNT_TYPE_TONE[k] || '').split(' ')[0]; // bg-*
          return (
            <div
              key={k}
              className={tone || 'bg-slate-300'}
              style={{ width: `${pct}%` }}
              title={`${at.label}: $${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })} (${pct.toFixed(1)}%)`}
            />
          );
        })}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {ACCOUNT_TYPES.map((at) => {
          const v = Number(byAccount[at.key] || 0);
          const pct = total > 0 ? (v / total) * 100 : 0;
          const Icon = at.icon;
          return (
            <div key={at.key} className="rounded-md border border-border bg-muted/20 px-2 py-1.5" data-testid={`ye-account-mix-${at.key}`}>
              <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
                {at.short}
              </div>
              <div className="font-mono text-sm font-bold">${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              <div className="text-[9px] text-muted-foreground">{pct.toFixed(1)}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
